import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Alert, Button, Card, Col, Form, Input, Modal, Row, Select, Space, Table, Tabs, Tag,
  Tooltip, Typography, message,
} from 'antd';
import Icon from '@mdi/react';
import dayjs from 'dayjs';
import {
  mdiCheckCircleOutline, mdiCloseCircleOutline, mdiContentCopy, mdiHistory, mdiKeyChain,
  mdiMessageTextOutline, mdiPlayCircleOutline, mdiPlusCircleOutline, mdiRefresh,
  mdiTrashCanOutline,
} from '@mdi/js';
import { UI54Table } from '@agpl/ui54kit';
import { ApiContext } from '../../api/ApiContextProvider';
import { urlAPI } from '../../api/api';
import useURLParams from '../../hooks/useURLParams';
import McpPromptExamples from './McpPromptExamples';

const { Title, Text, Paragraph } = Typography;

const MCP_SERVER_NAME = 'AUTH54MCP';

const METHOD_COLORS = {
  GET: 'blue',
  POST: 'green',
  PUT: 'gold',
  DELETE: 'red',
};

const TTL_OPTIONS = [
  { value: 30, label: '30 дней' },
  { value: 90, label: '90 дней (рекомендуется)' },
  { value: 180, label: '180 дней' },
  { value: 0, label: 'Без срока действия' },
];

const mcpUrl = () => `${urlAPI.replace(/\/$/, '')}/mcp/auth/`;

const AGENT_TABS = [
  {
    key: 'claude',
    label: 'Claude Code (VS Code)',
    steps: [
      '1. Установить расширение Claude Code в VS Code',
      '2. Включить VPN',
      '3. Войти в аккаунт Claude',
      '4. В корне вашего проекта создайте файл .mcp.json и добавьте туда сгенерированную конфигурацию',
      '5. Открыть чат Claude Code',
      '6. Ввести /mcp и выбрать сервис AUTH54MCP',
    ],
    file: '.mcp.json',

    server: (token) => ({
      type: 'http',
      url: mcpUrl(),
      headers: { Authorization: `Bearer ${token}` },
    }),
  },
  {
    key: 'roo',
    label: 'Roo Code (VS Code)',
    steps: [
      '1. Установить расширение Roo Code в VS Code',
      '2. Открыть панель Roo Code и нажать на иконку шестерёнки',
      '3. В открывшемся меню выбрать MCP Servers',
      "4. Далее нажмите 'Edit Global MCP' для открытия mcp_settings.json",
      '5. Скопируйте сгенерированную конфигурацию и вставьте в mcp_settings.json, затем сохраните файл',
      '6. Нажать Refresh MCP Servers в панели Roo Code',
    ],
    file: 'mcp_settings.json',
    // У Roo Code тип через дефис — streamable-http, в отличие от Cline.
    server: (token) => ({
      type: 'streamable-http',
      url: mcpUrl(),
      headers: { Authorization: `Bearer ${token}` },
      disabled: false,
    }),
  },
  {
    key: 'cline',
    label: 'Cline (VS Code)',
    steps: [
      '1. Откройте файл конфигурации MCP серверов Antigravity CLI',
      '2. Добавьте сгенерированную конфигурацию в словарь mcpServers',
      '3. Сохраните файл — Antigravity автоматически применит настройки',
    ],
    file: 'cline_mcp_settings.json',
    // Cline ждёт streamableHttp и явный disabled.
    server: (token) => ({
      type: 'streamableHttp',
      url: mcpUrl(),
      headers: { Authorization: `Bearer ${token}` },
      disabled: false,
    }),
  },
  {
    key: 'other',
    label: 'Другой агент',
    steps: [
      'Открыть конфигурацию MCP-серверов вашего агента',
      'Добавить HTTP-сервер с адресом и заголовком Authorization из окна создания токена',
    ],
    file: 'config.json',
    server: (token) => ({
      type: 'http',
      url: mcpUrl(),
      headers: { Authorization: `Bearer ${token}` },
    }),
  },
];

const agentByKey = (key) => AGENT_TABS.find((item) => item.key === key) || AGENT_TABS[0];

const configFor = (token, agentKey) => JSON.stringify(
  { mcpServers: { [MCP_SERVER_NAME]: agentByKey(agentKey).server(token) } },
  null,
  2,
);

function McpAccessPage() {
  const {
    requestGetMcpTokens,
    requestCreateMcpToken,
    requestRevokeMcpToken,
    requestGetMcpSessionRequests,
    requestDecideMcpSessionRequest,
    requestStartMcpSession,
    requestDeleteMcpSession,
    requestGetMcpCallLog,
  } = useContext(ApiContext);

  const { setSearchParams, getURLParams } = useURLParams();
  const { tab } = getURLParams();

  const [activeKey, setActiveKey] = useState('tokens');
  const [form] = Form.useForm();
  const [tokens, setTokens] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [agentTab, setAgentTab] = useState('claude');
  const [sessionRequests, setSessionRequests] = useState([]);
  const [deciding, setDeciding] = useState(null);
  const [starting, setStarting] = useState(null);
  const [deletingSession, setDeletingSession] = useState(null);
  const [modalAgent, setModalAgent] = useState('claude');
  const [calls, setCalls] = useState([]);
  const [callsFetching, setCallsFetching] = useState(false);
  const [callsPage, setCallsPage] = useState(1);
  const [callsLimit, setCallsLimit] = useState(25);

  const loadTokens = async () => {
    setFetching(true);
    try {
      const res = await requestGetMcpTokens();
      setTokens(res?.tokens || []);
    } catch {
      message.error('Не удалось загрузить список токенов');
    } finally {
      setFetching(false);
    }
  };

  const loadSessionRequests = async () => {
    try {
      const res = await requestGetMcpSessionRequests();
      setSessionRequests(res?.requests || []);
    } catch {
      setSessionRequests([]);
    }
  };

  const loadCalls = async () => {
    setCallsFetching(true);
    try {
      const res = await requestGetMcpCallLog();
      setCalls(res?.calls || []);
    } catch {
      setCalls([]);
    } finally {
      setCallsFetching(false);
    }
  };

  const onStartSession = async (record) => {
    setStarting(record.id);
    try {
      await requestStartMcpSession({ token_id: record.id });
      message.success('MCP session started');
      loadTokens();
    } catch {
      message.error('Could not start the session');
    } finally {
      setStarting(null);
    }
  };

  const onDeleteSession = async (record) => {
    setDeletingSession(record.id);
    try {
      await requestDeleteMcpSession({ token_id: record.id });
      message.success('MCP session deleted');
      loadTokens();
    } catch {
      message.error('Could not delete the session');
    } finally {
      setDeletingSession(null);
    }
  };

  useEffect(() => {
    if (!tab) {
      setSearchParams({ tab: 'tokens' });
    } else {
      setActiveKey(tab);
    }
  }, [tab]);

  useEffect(() => {
    loadTokens();
    loadSessionRequests();
    loadCalls();
    const timer = setInterval(() => {
      loadSessionRequests();
      loadTokens();
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const onDecide = async (record, decision) => {
    setDeciding(record.id);
    try {
      await requestDecideMcpSessionRequest({ id: record.id, decision });
      message.success(decision === 'approve' ? 'Запрос одобрен' : 'Запрос отклонён');
      loadSessionRequests();
    } catch {
      message.error('Не удалось обработать запрос');
    } finally {
      setDeciding(null);
    }
  };

  const onCreate = async ({ label, ttlDays }) => {
    setCreating(true);
    try {
      const res = await requestCreateMcpToken({ label, ttl_days: ttlDays });
      setModalAgent(agentTab);
      setCreatedToken(res);
      form.resetFields();
      loadTokens();
    } catch {
      message.error('Не удалось создать токен');
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = (record) => Modal.confirm({
    title: 'Отозвать токен?',
    content: `Токен «${record.label}» перестанет работать немедленно. Действие необратимо.`,
    okText: 'Отозвать',
    okButtonProps: { danger: true },
    cancelText: 'Отмена',
    onOk: async () => {
      try {
        await requestRevokeMcpToken({ id: record.id });
        message.success('Токен отозван');
        loadTokens();
      } catch {
        message.error('Не удалось отозвать токен');
      }
    },
  });

  const onTabClick = (key) => {
    setSearchParams({ tab: key });
    if (key === 'calls') {
      loadCalls();
    }
  };

  const tabLabel = (label, iconPath) => (
    <span>
      <Icon path={iconPath} size={0.9} className="mr-1 align-middle" />
      {label}
    </span>
  );

  const copy = (value, what) => {
    navigator.clipboard.writeText(value);
    message.success(`${what} скопирован`);
  };

  const formatDate = (value) => (value
    ? new Date(value).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
    : '—');

  const isExpired = (record) => record.expires_at && new Date(record.expires_at) < new Date();

  const callsDataSource = useMemo(
    () => calls.slice((callsPage - 1) * callsLimit, callsPage * callsLimit),
    [calls, callsPage, callsLimit],
  );

  const callColumns = useMemo(() => [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      className: 'p-1',
      render: (value) => (
        <span className="text-primary text-size-4">
          {dayjs(value).format('YYYY-MM-DD HH:mm:ss')}
        </span>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      className: 'p-3',
      render: (value) => <Tag color={METHOD_COLORS[value] || 'default'}>{value}</Tag>,
    },
    {
      title: 'Endpoint',
      dataIndex: 'path',
      key: 'path',
      className: 'p-3 max-width-200px',
      ellipsis: true,
      render: (value) => (
        <Tooltip title={value}>
          <Text code>{value}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Mode',
      dataIndex: 'mode',
      key: 'mode',
      className: 'p-3',
      render: (value) => (value
        ? <Tag color={value === 'write' ? 'orange' : 'blue'}>{value}</Tag>
        : <Text type="secondary">—</Text>),
    },
    {
      title: 'Result',
      key: 'result',
      className: 'p-3',
      render: (_value, record) => {
        if (record.status_code) {
          const ok = record.status_code < 400;
          return <Tag color={ok ? 'green' : 'red'}>{record.status_code}</Tag>;
        }
        return <Tag color="red">blocked</Tag>;
      },
    },
    {
      title: 'Details',
      dataIndex: 'error',
      key: 'error',
      className: 'p-3 max-width-200px',
      ellipsis: true,
      render: (value) => (value
        ? (
          <Tooltip title={value}>
            <span className="colorTextGrey">{value}</span>
          </Tooltip>
        )
        : <Text type="secondary">—</Text>),
    },
    {
      title: 'Token',
      dataIndex: 'token_id',
      key: 'token_id',
      className: 'p-3',
      render: (value) => {
        const token = tokens.find((item) => item.id === value);
        return <span className="colorTextGrey">{token?.label || `#${value}`}</span>;
      },
    },
    {
      title: 'Agent',
      dataIndex: 'operator',
      key: 'operator',
      className: 'p-3',
      ellipsis: true,
      render: (value) => <span className="colorTextGrey">{value || '—'}</span>,
    },
  ], [tokens]);

  const columns = useMemo(() => [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_v, _r, index) => index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'label',
      key: 'label',
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: 'Server',
      key: 'server',
      width: 140,
      render: () => <Tag color="blue">{MCP_SERVER_NAME}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: formatDate,
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 170,
      render: (value) => (value ? formatDate(value) : <Text type="secondary">never</Text>),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_v, record) => {
        if (record.revoked_at) return <Tag color="default">Revoked</Tag>;
        if (isExpired(record)) return <Tag color="orange">Expired</Tag>;
        return <Tag color="green">Active</Tag>;
      },
    },
    {
      title: 'Session',
      key: 'session',
      width: 250,
      render: (_v, record) => {
        if (record.revoked_at || isExpired(record)) return <Text type="secondary">—</Text>;
        if (record.session_active) {
          return (
            <Space size={4}>
              <Tooltip title={`until ${formatDate(record.session_expires_at)}`}>
                <Tag color="green" className="m-0">Active</Tag>
              </Tooltip>
              <Button
                danger
                size="small"
                loading={deletingSession === record.id}
                icon={<Icon path={mdiCloseCircleOutline} size={0.7} />}
                onClick={() => onDeleteSession(record)}
              >
                Delete session
              </Button>
            </Space>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            loading={starting === record.id}
            icon={<Icon path={mdiPlayCircleOutline} size={0.8} />}
            onClick={() => onStartSession(record)}
          >
            Start MCP session
          </Button>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_v, record) => (record.revoked_at ? null : (
        <Button
          danger
          size="small"
          icon={<Icon path={mdiTrashCanOutline} size={0.7} />}
          onClick={() => onRevoke(record)}
        >
          Revoke
        </Button>
      )),
    },
  ], [starting, deletingSession]);

  const activeTab = agentByKey(agentTab);

  return (
    <div className="p-4">
      <Title level={3} className="mb-1">
        <Icon path={mdiKeyChain} size={1} className="mr-2 align-middle" />
        MCP Access
      </Title>
      <Paragraph type="secondary">
        Токены доступа для AI-ассистентов. Агент работает от вашего имени: его права равны вашим правам в биоме.
      </Paragraph>

      {sessionRequests.length > 0 && (
      <Card
        size="small"
        className="mb-4"
        title={(
          <span>
            Запросы на сессию
            <Tag color="orange" className="ml-2">{sessionRequests.length}</Tag>
          </span>
        )}
        extra={(
          <Button
            type="text"
            size="small"
            icon={<Icon path={mdiRefresh} size={0.8} />}
            onClick={loadSessionRequests}
          />
        )}
      >
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message="Агент просит доступ к API от вашего имени"
          description="Одобряйте, только если сами сейчас работаете с агентом. Сессия выдаётся на 6 часов и действует с вашими правами."
        />
        {sessionRequests.map((item) => (
          <div
            key={item.id}
            className="d-flex flex-wrap align-items-center justify-content-between mb-2 p-2"
            style={{ background: '#fafafa', borderRadius: 4 }}
          >
            <div>
              <Text strong>{item.token_label || `токен #${item.token_id}`}</Text>
              <Text type="secondary" className="ml-2">
                запрос #
                {item.id}
                {' · создан '}
                {formatDate(item.created_at)}
                {' · истекает '}
                {formatDate(item.expires_at)}
              </Text>
            </div>
            <Space>
              <Button
                type="primary"
                size="small"
                loading={deciding === item.id}
                icon={<Icon path={mdiCheckCircleOutline} size={0.8} />}
                onClick={() => onDecide(item, 'approve')}
              >
                Одобрить
              </Button>
              <Button
                danger
                size="small"
                loading={deciding === item.id}
                icon={<Icon path={mdiCloseCircleOutline} size={0.8} />}
                onClick={() => onDecide(item, 'deny')}
              >
                Отклонить
              </Button>
            </Space>
          </div>
        ))}
      </Card>
      )}

      <Tabs
        activeKey={activeKey}
        onTabClick={onTabClick}
        destroyInactiveTabPane
        items={[
          {
            key: 'tokens',
            label: tabLabel('MCP Tokens', mdiKeyChain),
            children: (
              <>
                <Card
                  title="Инструкция по настройке AI-ассистента"
                  className="mb-4"
                  size="small"
                >
                  <Tabs
                    activeKey={agentTab}
                    onChange={setAgentTab}
                    items={AGENT_TABS.map((item) => ({ key: item.key, label: item.label }))}
                  />
                  <ol className="mb-3 pl-4">
                    {activeTab.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ol>
                  <Text type="secondary">Файл конфигурации: </Text>
                  <Text code>{activeTab.file}</Text>
                  {activeTab.note && (
                    <Alert
                      type="info"
                      showIcon
                      className="mt-3"
                      message={activeTab.note}
                    />
                  )}
                  <Alert
                    type="info"
                    showIcon
                    className="mt-3"
                    message="Примечание: После изменений файла .mcp.json всегда нужно открывать новый чат."
                  />
                </Card>
                <Card title="Создать новый MCP-токен" className="mb-4" size="small">
                  <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ ttlDays: 90, label: 'Default Agent Token' }}
                    onFinish={onCreate}
                  >
                    <Row gutter={16}>
                      <Col xs={24} md={10}>
                        <Form.Item
                          name="label"
                          label="Название токена"
                          rules={[{ required: true, message: 'Введите название' }]}
                        >
                          <Input placeholder="Default Agent Token" allowClear />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item name="ttlDays" label="Срок действия">
                          <Select options={TTL_OPTIONS} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={6}>
                        <Form.Item label=" ">
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={creating}
                            icon={<Icon path={mdiPlusCircleOutline} size={0.8} />}
                          >
                            Сгенерировать токен
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Card>

                <Card title="Мои токены" size="small">
                  <Table
                    rowKey="id"
                    size="small"
                    loading={fetching}
                    dataSource={tokens}
                    columns={columns}
                    pagination={false}
                    locale={{ emptyText: 'Токенов пока нет' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'prompts',
            label: tabLabel('Prompt Examples', mdiMessageTextOutline),
            children: <McpPromptExamples />,
          },
          {
            key: 'calls',
            label: tabLabel('Call log', mdiHistory),
            children: (
              <Card
                size="small"
                title="Запросы агентов к API auth54"
                extra={(
                  <Button
                    size="small"
                    loading={callsFetching}
                    icon={<Icon path={mdiRefresh} size={0.8} />}
                    onClick={loadCalls}
                  >
                    Обновить
                  </Button>
                )}
              >
                <UI54Table
                  loading={callsFetching}
                  dataSource={callsDataSource}
                  columns={callColumns}
                  rowKey="id"
                  pagination={{
                    total: calls.length,
                    current: callsPage,
                    pageSize: callsLimit,
                    pageSizeOptions: [25, 50],
                    onShowSizeChange: (_current, size) => {
                      setCallsPage(1);
                      setCallsLimit(size);
                    },
                    onChange: setCallsPage,
                  }}
                  tableLayout="auto"
                  scroll={{ x: 'max-content' }}
                  locale={{ emptyText: 'Агенты пока не обращались к API' }}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        open={!!createdToken}
        title="Токен создан"
        onCancel={() => setCreatedToken(null)}
        footer={[
          <Button key="done" type="primary" onClick={() => setCreatedToken(null)}>
            Готово, я настроил агента
          </Button>,
        ]}
        width={720}
      >
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message="Сохраните токен прямо сейчас"
          description="Из соображений безопасности токен показывается только один раз!"
        />
        <Text type="secondary">Ваш токен:</Text>
        <Space.Compact className="w-100 mb-3 mt-1" style={{ display: 'flex' }}>
          <Input readOnly value={createdToken?.token} />
          <Button
            type="primary"
            icon={<Icon path={mdiContentCopy} size={0.8} />}
            onClick={() => copy(createdToken?.token, 'Токен')}
          >
            Копировать
          </Button>
        </Space.Compact>
        <Text type="secondary">Готовая конфигурация для вашего агента:</Text>
        <Tabs
          size="small"
          className="mt-1"
          activeKey={modalAgent}
          onChange={setModalAgent}
          items={AGENT_TABS.map((item) => ({ key: item.key, label: item.label }))}
        />
        <Text type="secondary">Файл: </Text>
        <Text code>{agentByKey(modalAgent).file}</Text>
        <pre className="mt-2 p-3" style={{ background: '#f5f5f5', borderRadius: 4, overflowX: 'auto' }}>
          {createdToken ? configFor(createdToken.token, modalAgent) : ''}
        </pre>
        <Button
          icon={<Icon path={mdiContentCopy} size={0.8} />}
          onClick={() => copy(configFor(createdToken?.token, modalAgent), 'Конфиг')}
        >
          Скопировать конфиг
        </Button>
      </Modal>
    </div>
  );
}

export default McpAccessPage;
